<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import XlPopover from '../xl/XlPopover.vue';

/**
 * The overflow at the end of a content row. The caller passes the items; this
 * only knows how to open, list and report a pick.
 *
 * `toggle` carries the open state out so the row can mark itself while its own
 * menu is up — the panel is teleported to the body, so nothing about it points
 * back at the row it came from.
 */
defineProps({
  // [{ key, label, danger?: Boolean, disabled?: Boolean }]
  items: { type: Array, required: true },
  label: { type: String, default: 'More actions' },
});
const emit = defineEmits(['select', 'toggle']);

const open = ref(false);
const anchor = ref(null);

// Every close path runs through the ref — a pick, Escape, a click outside, a
// route change — so the row is told from here rather than from each of them.
watch(open, (isOpen) => emit('toggle', isOpen));

// A row can vanish under an open menu with no click to close it: a banner
// rolling live → ended on the minute tick drops out of a filtered page while
// its panel is up. `v-if` then unmounts this without the watcher ever firing,
// and the caller is left holding an id it will highlight when that row comes
// back. Say so on the way out.
onBeforeUnmount(() => { if (open.value) emit('toggle', false); });

const pick = (item) => {
  if (item.disabled) return;
  open.value = false;
  emit('select', item.key);
};
</script>

<template>
  <span class="content-menu">
    <button
      ref="anchor"
      type="button"
      class="content-menu__trigger"
      :aria-label="label"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
    </button>
    <XlPopover :open="open" :anchor="anchor" align="end" @update:open="open = $event">
      <div role="menu" class="content-menu__list" :aria-label="label">
        <button
          v-for="item in items"
          :key="item.key"
          type="button"
          role="menuitem"
          class="content-menu__item"
          :class="{ 'is-danger': item.danger }"
          :disabled="item.disabled"
          @click="pick(item)"
        >{{ item.label }}</button>
      </div>
    </XlPopover>
  </span>
</template>

<style scoped>
.content-menu { display: inline-flex; }

.content-menu__trigger {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--xl-radius-sm, 8px);
  background: transparent;
  color: var(--xl-color-secondary-text, #49454f);
  cursor: pointer;
}

.content-menu__trigger:hover,
.content-menu__trigger[aria-expanded="true"] {
  background: color-mix(in srgb, var(--xl-color-primary, #5b3fd1) 8%, white);
  color: var(--xl-color-primary-text, #1d1b20);
}

.content-menu__list {
  display: flex;
  flex-direction: column;
  margin: -8px;
  min-width: 180px;
}

.content-menu__item {
  text-align: left;
  padding: 9px 12px;
  border: 0;
  border-radius: var(--xl-radius-sm, 8px);
  background: transparent;
  font: inherit;
  font-size: var(--xl-type-body-medium-size, 14px);
  color: var(--xl-color-primary-text, #1d1b20);
  cursor: pointer;
}

.content-menu__item:hover:not(:disabled) { background: color-mix(in srgb, var(--xl-color-primary, #5b3fd1) 8%, white); }
.content-menu__item:disabled { opacity: 0.5; cursor: default; }
.content-menu__item.is-danger { color: var(--xl-color-error, #ba1a1a); }
</style>
